import { supabase } from '../lib/supabase';

const SETTINGS_TABLE = 'site_settings';

export const settingsService = {
  // Map DB row (snake_case) to app format (camelCase)
  mapDbToApp(row) {
    if (!row) return null;
    return {
      businessName: row.business_name ?? '',
      email: row.email ?? '',
      phone: row.phone ?? '',
      address: row.address ?? '',
      businessHours: row.business_hours ?? this.getDefaultSettings().businessHours,
      salesTaxRate: typeof row.sales_tax_rate === 'number' ? row.sales_tax_rate : parseFloat(row.sales_tax_rate ?? this.getDefaultSettings().salesTaxRate),
      docFee: typeof row.doc_fee === 'number' ? row.doc_fee : parseFloat(row.doc_fee ?? this.getDefaultSettings().docFee),
      defaultFinanceRate: typeof row.default_finance_rate === 'number' ? row.default_finance_rate : parseFloat(row.default_finance_rate ?? this.getDefaultSettings().defaultFinanceRate),
      emailNotifications: row.email_notifications ?? true,
      smsNotifications: row.sms_notifications ?? false,
      appointmentReminders: row.appointment_reminders ?? true,
      facebook: row.facebook ?? '',
      instagram: row.instagram ?? '',
      twitter: row.twitter ?? '',
      youtube: row.youtube ?? '',
      metaTitle: row.meta_title ?? this.getDefaultSettings().metaTitle,
      metaDescription: row.meta_description ?? this.getDefaultSettings().metaDescription,
      keywords: row.keywords ?? this.getDefaultSettings().keywords,
      id: row.id,
      created_at: row.created_at,
      updated_at: row.updated_at,
    };
  },

  // Map app format (camelCase) to DB row (snake_case)
  mapAppToDb(settings) {
    return {
      business_name: settings.businessName,
      email: settings.email,
      phone: settings.phone,
      address: settings.address,
      business_hours: settings.businessHours,
      sales_tax_rate: settings.salesTaxRate,
      doc_fee: settings.docFee,
      default_finance_rate: settings.defaultFinanceRate,
      email_notifications: settings.emailNotifications,
      sms_notifications: settings.smsNotifications,
      appointment_reminders: settings.appointmentReminders,
      facebook: settings.facebook,
      instagram: settings.instagram,
      twitter: settings.twitter,
      youtube: settings.youtube,
      meta_title: settings.metaTitle,
      meta_description: settings.metaDescription,
      keywords: settings.keywords,
    };
  },
  // Get all settings
  async getSettings() {
    try {
      const { data, error } = await supabase
        .from(SETTINGS_TABLE)
        .select('*')
        .single();
      
      // If table missing (42P01) or no rows (PGRST116), return defaults
      if (error && (error.code === 'PGRST116' || error.code === '42P01')) {
        return { data: this.getDefaultSettings(), error: null };
      }

      // Merge with defaults and map to camelCase to ensure required fields exist
      const defaults = this.getDefaultSettings();
      const mapped = data ? this.mapDbToApp(data) : null;
      const merged = mapped
        ? { ...defaults, ...mapped, businessHours: { ...defaults.businessHours, ...(mapped.businessHours || {}) } }
        : defaults;

      return { data: merged, error: null };
    } catch (err) {
      console.error('Error fetching settings:', err);
      return { data: null, error: err };
    }
  },

  // Save settings
  async saveSettings(settings) {
    try {
      // First check if settings exist
      const { data: existing } = await supabase
        .from(SETTINGS_TABLE)
        .select('id')
        .single();

      let result;
      if (existing) {
        // Update existing settings
        result = await supabase
          .from(SETTINGS_TABLE)
          .update({
            ...this.mapAppToDb(settings),
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id)
          .select()
          .single();
      } else {
        // Insert new settings
        result = await supabase
          .from(SETTINGS_TABLE)
          .insert({
            ...this.mapAppToDb(settings),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();
      }

      return result;
    } catch (err) {
      console.error('Error saving settings:', err);
      return { data: null, error: err };
    }
  },

  // Get default settings
  getDefaultSettings() {
    return {
      // Business Information
      businessName: 'ShedX',
      email: 'info@seyardsolutions.com',
      phone: '',
      address: ',  ',
      
      // Business Hours
      businessHours: {
        monday: { open: '8:00 AM', close: '5:00 PM' },
        tuesday: { open: '8:00 AM', close: '5:00 PM' },
        wednesday: { open: '8:00 AM', close: '5:00 PM' },
        thursday: { open: '8:00 AM', close: '5:00 PM' },
        friday: { open: '8:00 AM', close: '5:00 PM' },
        saturday: { open: '9:00 AM', close: '4:00 PM' },
        sunday: { open: 'Closed', close: 'Closed' },
      },
      
      // Pricing Settings
      salesTaxRate: 8.25,
      docFee: 150,
      defaultFinanceRate: 4.9,
      
      // Notification Settings
      emailNotifications: true,
      smsNotifications: false,
      appointmentReminders: true,
      
      // Social Media
      facebook: '',
      instagram: '',
      twitter: '',
      youtube: '',
      
      // SEO Settings
      metaTitle: 'ShedX - Storage Sheds & Outdoor Buildings',
      metaDescription: 'Browse quality storage sheds and outdoor buildings from verified dealers. Find your perfect shed today.',
      keywords: 'sheds, storage sheds, outdoor buildings, barns, garden sheds'
    };
  }
};

export default settingsService;
